import { useEffect, useState } from 'react';
import { adminRequested } from '../config';
import { isLocalHost } from './isLocalHost';

/**
 * Ways into the admin view:
 *   ?admin=1        — the one to put on a laminated card for show night
 *   ctrl+shift+A    — laptop, during setup
 *   taps in the top-left corner — the kiosk has no keyboard
 *
 * The corner gesture is small and fast enough that no guest will find it by
 * accident, and it does not interfere with anything on screen.
 *
 * The view reports supabase URLs, device IDs, masked phone numbers and the raw
 * queue, so it must not be reachable by a guest who guesses ?admin=1 or happens
 * to prod a corner. Hence the gate.
 *
 * **The gate is the caller's to decide** — it used to be `isLocalHost()` here,
 * which had the side effect of closing the view on the unit itself, where the
 * shell serves from appassets.androidplatform.net rather than localhost. That
 * left an operator on show night with no way to see queue depth or drain the
 * queue, which is the one thing the screen exists for. `config.adminEnabled`
 * now decides, and it is true on the kiosk — see src/config/flags.ts.
 *
 * On the kiosk the gesture is deliberately harder: five taps rather than three,
 * because a 49" panel gets prodded by strangers all day and a three-tap
 * accident in front of a queue of guests would put phone numbers on screen.
 */
const CORNER_PX = 100;
const TAP_WINDOW_MS = 2000;
const TAPS_WEB = 3;
export const TAPS_KIOSK = 5;

export function useAdminToggle(
  allowed: boolean = isLocalHost(),
  /** Kiosk raises the tap count. Defaults to the web gesture. */
  tapsNeeded: number = TAPS_WEB,
): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState<boolean>(() => allowed && adminRequested());

  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };

    let taps: number[] = [];
    const onPointer = (e: PointerEvent) => {
      if (e.clientX > CORNER_PX || e.clientY > CORNER_PX) return;
      const now = e.timeStamp;
      taps = [...taps.filter((t) => now - t < TAP_WINDOW_MS), now];
      if (taps.length >= tapsNeeded) {
        taps = [];
        setOpen((v) => !v);
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer, { capture: true });
    };
  }, [allowed, tapsNeeded]);

  return [allowed && open, setOpen];
}
